import { ccfRoutes } from '@app/ccf/ccf-routes';

/**
 * The five Scope 1 & 2 activity categories that support the Excel importer.
 * Each maps one workbook sheet → one synchronous `imports/process/*` endpoint.
 */
export type Scope1And2CategorySlug =
  | 'electricity'
  | 'heat'
  | 'stationary-combustion'
  | 'mobile-combustion'
  | 'direct-gas-emissions';

export interface Scope1And2CategoryConfig {
  slug: Scope1And2CategorySlug;
  /** Human-readable label used in copy. */
  label: string;
  /** The exact tab name in the template workbook the user should select. */
  sheetName: string;
  /** Whether the category sheet has a Fuel column. */
  hasFuel: boolean;
  /** Whether the category sheet has a Gas column. */
  hasGas: boolean;
  /** Whether the category sheet has a Source column (direct gas only). */
  hasSource: boolean;
  /** Mobile combustion makes site optional; every other category requires it. */
  siteOptional: boolean;
  /** Mobile combustion only accepts fuels flagged `include_mobile_combustion`. */
  mobileFuelsOnly: boolean;
}

export const SCOPE_1_2_IMPORT_CONFIG: Record<
  Scope1And2CategorySlug,
  Scope1And2CategoryConfig
> = {
  electricity: {
    slug: 'electricity',
    label: 'Electricity',
    sheetName: 'S2 - Electricity',
    hasFuel: false,
    hasGas: false,
    hasSource: false,
    siteOptional: false,
    mobileFuelsOnly: false,
  },
  heat: {
    slug: 'heat',
    label: 'Heat & steam',
    sheetName: 'S2 - Heat & Steam',
    hasFuel: false,
    hasGas: false,
    hasSource: false,
    siteOptional: false,
    mobileFuelsOnly: false,
  },
  'stationary-combustion': {
    slug: 'stationary-combustion',
    label: 'Stationary combustion',
    sheetName: 'S1 - Stationary Combustion',
    hasFuel: true,
    hasGas: false,
    hasSource: false,
    siteOptional: false,
    mobileFuelsOnly: false,
  },
  'mobile-combustion': {
    slug: 'mobile-combustion',
    label: 'Mobile combustion',
    sheetName: 'S1 - Mobile Combustion',
    hasFuel: true,
    hasGas: false,
    hasSource: false,
    siteOptional: true,
    mobileFuelsOnly: true,
  },
  'direct-gas-emissions': {
    slug: 'direct-gas-emissions',
    label: 'Direct gas emissions',
    sheetName: 'S1 - Direct Gas Emissions',
    hasFuel: false,
    hasGas: true,
    hasSource: true,
    siteOptional: false,
    mobileFuelsOnly: false,
  },
};

export const SCOPE_1_2_CATEGORY_SLUGS = Object.keys(
  SCOPE_1_2_IMPORT_CONFIG
) as Scope1And2CategorySlug[];

export function isScope1And2Category(
  slug?: string
): slug is Scope1And2CategorySlug {
  return !!slug && slug in SCOPE_1_2_IMPORT_CONFIG;
}

/** Returns the config for a slug, or undefined if it isn't a Scope 1/2 category. */
export function getScope1And2Config(
  slug?: string
): Scope1And2CategoryConfig | undefined {
  return isScope1And2Category(slug) ? SCOPE_1_2_IMPORT_CONFIG[slug] : undefined;
}

/**
 * The category page route an importer belongs to (for back / close navigation).
 * Resolved lazily inside a function so `ccfRoutes` is never read at module
 * evaluation time — that would create a circular-import init error, since
 * `ccf-routes` imports this module via the importer pages.
 */
export function getScope1And2CategoryRoute(
  slug: Scope1And2CategorySlug
): string {
  switch (slug) {
    case 'electricity':
      return ccfRoutes.ELECTRICITY;
    case 'heat':
      return ccfRoutes.HEATING;
    case 'stationary-combustion':
      return ccfRoutes.STATIONARY_COMBUSTION;
    case 'mobile-combustion':
      return ccfRoutes.MOBILE_COMBUSTION;
    case 'direct-gas-emissions':
      return ccfRoutes.DIRECT_GAS;
  }
}
